import { EditorNodeName } from "@/enums/customAttributes";
import { SERVICE_CUSTOM } from "@/helpers/constants";

import type { EditorNode } from "@/models/TextEditor";

const createNodeSignature = (node: EditorNode): string => {
  if (!node.attributeId) {
    return node.content;
  }

  if (node.attributeId !== SERVICE_CUSTOM) {
    return node.attributeId;
  }

  const customAttributeId = (node.context as { value?: { id?: string } })?.value
    ?.id;

  return customAttributeId
    ? `${SERVICE_CUSTOM}:${customAttributeId}`
    : node.attributeId;
};

const findLCS = (
  oldSignatures: string[],
  newSignatures: string[]
): Map<number, number> => {
  const oldLength = oldSignatures.length;
  const newLength = newSignatures.length;

  const lcsTable: number[][] = Array(oldLength + 1)
    .fill(null)
    .map(() => Array(newLength + 1).fill(0));

  for (let oldIdx = 1; oldIdx <= oldLength; oldIdx++) {
    for (let newIdx = 1; newIdx <= newLength; newIdx++) {
      if (oldSignatures[oldIdx - 1] === newSignatures[newIdx - 1]) {
        lcsTable[oldIdx][newIdx] = lcsTable[oldIdx - 1][newIdx - 1] + 1;
      } else {
        lcsTable[oldIdx][newIdx] = Math.max(
          lcsTable[oldIdx - 1][newIdx],
          lcsTable[oldIdx][newIdx - 1]
        );
      }
    }
  }

  const matches = new Map<number, number>();
  let oldIdx = oldLength;
  let newIdx = newLength;

  while (oldIdx > 0 && newIdx > 0) {
    if (oldSignatures[oldIdx - 1] === newSignatures[newIdx - 1]) {
      matches.set(newIdx - 1, oldIdx - 1);
      oldIdx--;
      newIdx--;
    } else if (lcsTable[oldIdx - 1][newIdx] > lcsTable[oldIdx][newIdx - 1]) {
      oldIdx--;
    } else {
      newIdx--;
    }
  }

  return matches;
};

export const mergeNodeIdentities = (
  oldNodes: EditorNode[],
  newNodes: EditorNode[]
): EditorNode[] => {
  if (!oldNodes.length || !newNodes.length) {
    return newNodes;
  }

  const oldSignatures = oldNodes.map(createNodeSignature);
  const newSignatures = newNodes.map(createNodeSignature);

  const matches = findLCS(oldSignatures, newSignatures);

  const mergedNodes = newNodes.map((newNode, newIdx) => {
    const matchedOldIdx = matches.get(newIdx);

    if (matchedOldIdx !== undefined) {
      const oldNode = oldNodes[matchedOldIdx];

      // Preserve attribute type if old node was an attribute but new is unrecognized
      const shouldPreserveAttributeType =
        oldNode.type === EditorNodeName.attribute &&
        newNode.type === EditorNodeName.unrecognized &&
        oldNode.attributeId;

      const hasNewContext =
        newNode.context && Object.keys(newNode.context).length;
      const hasOldContext =
        oldNode.context && Object.keys(oldNode.context).length;

      // For attributes, preserve content (display name) from old node
      // This is important for custom attributes where the display name can't be looked up
      const shouldPreserveContent =
        oldNode.type === EditorNodeName.attribute &&
        oldNode.attributeId &&
        oldNode.content !== newNode.content;

      return {
        ...newNode,
        id: oldNode.id,
        type: shouldPreserveAttributeType
          ? EditorNodeName.attribute
          : newNode.type,
        content: shouldPreserveContent ? oldNode.content : newNode.content,
        uiId: oldNode.uiId,
        isSelected: oldNode.isSelected,
        attributeId: newNode.attributeId || oldNode.attributeId,
        context: hasNewContext
          ? newNode.context
          : hasOldContext
            ? oldNode.context
            : undefined,
      };
    }

    return newNode;
  });

  return mergedNodes;
};
